import { AppCard } from "@/components/ui";
import { ArrowUpRight, Check, Loader2, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/Button";

const STAGES = ["Reading your profile", "Scoring categories", "Assessing readiness", "Writing insights"] as const;

/** Rotating reassurance lines so the wait always feels like real work is happening. */
const REASSURANCE = [
  "Reading your onboarding answers…",
  "Scoring against our readiness rubric…",
  "Weighing your strengths and gaps…",
  "Matching suitable funding types…",
  "Drafting your recommendations…",
  "Almost there — polishing the details…",
] as const;

const REASSURANCE_INTERVAL_MS = 3_500;

export type TProcessingCardVariant = "analysis" | "reevaluation";

type TProcessingCardProps = {
  variant?: TProcessingCardVariant;
  onNotifyWhenFinished?: () => void;
  minimized?: boolean;
  onExpand?: () => void;
};

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  return `${Math.floor(totalSec / 60)}:${String(totalSec % 60).padStart(2, "0")}`;
}

export function ProcessingCard({
  variant = "analysis",
  onNotifyWhenFinished,
  minimized = false,
  onExpand,
}: TProcessingCardProps) {
  const isReevaluation = variant === "reevaluation";
  // Expected duration paces the progress bar/stepper; the bar eases toward ~95% and
  // never completes until the real result arrives, so it reads as honest progress.
  const expectedMs = isReevaluation ? 10 * 60_000 : 75_000;

  const [elapsedMs, setElapsedMs] = useState(0);
  const [reassuranceIndex, setReassuranceIndex] = useState(0);
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    const id = setInterval(() => setElapsedMs(Date.now() - startRef.current), 250);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setReassuranceIndex((p) => p + 1), REASSURANCE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const progress = Math.min(0.95, 1 - Math.exp(-elapsedMs / (expectedMs * 0.45)));
  const pct = Math.round(progress * 100);
  const activeStage = Math.min(STAGES.length - 1, Math.floor(progress * STAGES.length));
  const reassurance = REASSURANCE[reassuranceIndex % REASSURANCE.length];

  const handleNotify = useCallback(() => {
    onNotifyWhenFinished?.();
  }, [onNotifyWhenFinished]);

  const title = isReevaluation ? "Re-evaluating readiness" : "Company Readiness Analysis";
  const subtitle = isReevaluation
    ? "Re-evaluation runs in the background. We'll notify you when it's ready."
    : "Analyzing your company's investment readiness from your onboarding data.";
  const minimizedMessage = isReevaluation
    ? "Re-evaluation in progress. We'll notify you when it's ready."
    : "Readiness analysis in progress. We'll notify you when it's ready.";

  if (minimized) {
    return (
      <AppCard className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">{minimizedMessage}</p>
          {onExpand && (
            <Button
              variant="link"
              size="sm"
              onClick={onExpand}
              className="text-primary"
            >
              Show
            </Button>
          )}
        </div>
      </AppCard>
    );
  }

  return (
    <AppCard className="p-4">
      {/* Header — matches InvestorReadinessFeature card */}
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary sm:h-9 sm:w-9">
          <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground sm:text-base md:text-lg">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      {/* Live progress: eased bar + current activity + elapsed timer */}
      <div className="rounded-xl border border-border bg-muted/40 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
            <span className="truncate text-sm font-medium text-foreground">{STAGES[activeStage]}</span>
          </div>
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{formatElapsed(elapsedMs)}</span>
        </div>

        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-700 ease-out"
            style={{ width: `${Math.max(6, pct)}%` }}
          />
        </div>

        {/* Rotating reassurance so it always feels active */}
        <p
          key={reassuranceIndex}
          className="mt-2.5 animate-in fade-in text-xs text-muted-foreground duration-500"
        >
          {reassurance}
        </p>
      </div>

      {/* Vertical stepper with done / active / pending states */}
      <ol className="mt-4 space-y-2.5">
        {STAGES.map((label, i) => {
          const done = i < activeStage;
          const active = i === activeStage;
          return (
            <li
              key={label}
              className="flex items-center gap-2.5"
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors ${
                  done
                    ? "bg-primary text-primary-foreground"
                    : active
                      ? "bg-primary/10 text-primary ring-1 ring-inset ring-primary/30"
                      : "bg-muted text-transparent"
                }`}
              >
                {done ? (
                  <Check className="h-3 w-3" />
                ) : active ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                )}
              </span>
              <span
                className={`text-sm ${done ? "text-muted-foreground" : active ? "font-medium text-foreground" : "text-muted-foreground/70"}`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>

      <p className="mt-4 text-xs text-muted-foreground">
        {isReevaluation
          ? "This runs in the background and can take about 8–15 minutes — you can keep working."
          : "This usually takes under a minute. You can keep this tab open."}
      </p>

      {/* Notify me when finished */}
      {onNotifyWhenFinished && (
        <div className="mt-3">
          <Button
            variant="outline"
            className="w-full"
            onClick={handleNotify}
          >
            <ArrowUpRight className="h-4 w-4" />
            Notify me when finished
          </Button>
        </div>
      )}
    </AppCard>
  );
}
